import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { Skeleton, StatsGridSkeleton } from '../common/Skeleton';
import { generateCandidateLink, generateInterviewerLink } from '../../utils/roomUtils';

const parseUTC = (dateStr) => {
  if (!dateStr) return new Date();
  let str = String(dateStr).trim().replace(' ', 'T');
  if (str.endsWith('Z')) str = str.slice(0, -1);
  return new Date(str);
};

export default function HROverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalRequisitions: 0,
    openRequisitions: 0,
    totalScreened: 0,
    totalScheduled: 0,
    totalExits: 0,
    pendingExits: 0
  });
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  useEffect(() => {
    const fetchOverviewStats = async () => {
      try {
        setLoading(true);
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        const userId = user.id || null;

        const [reqRes, exitsRes, candRes, roomsRes, globalReqRes, scheduledRes, statsRes] = await Promise.allSettled([
          api.get('/api/requisitions/overview-stats'),
          api.get('/api/employees/exits/all'),
          api.get('/api/screening/candidates/all'),
          api.get('/api/auth/interview-rooms'),
          api.get('/api/requisitions/global'),
          api.get('/api/interviews/all-scheduled'),
          userId ? api.get(`/api/auth/interview-rooms/stats/${userId}`) : Promise.reject()
        ]);

        const reqData = reqRes.status === 'fulfilled' ? reqRes.value.data : null;
        const exitsData = exitsRes.status === 'fulfilled' && Array.isArray(exitsRes.value.data) ? exitsRes.value.data : [];
        const candData = candRes.status === 'fulfilled' && Array.isArray(candRes.value.data) ? candRes.value.data : [];
        const roomsData = roomsRes.status === 'fulfilled' && Array.isArray(roomsRes.value.data) ? roomsRes.value.data : [];
        const globalReqData = globalReqRes.status === 'fulfilled' && Array.isArray(globalReqRes.value.data) ? globalReqRes.value.data : [];
        const scheduledData = scheduledRes.status === 'fulfilled' && Array.isArray(scheduledRes.value.data) ? scheduledRes.value.data : [];
        const statsObj = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
        const statsData = statsObj?.total_list || statsObj?.pending_list || [];

        // Deduplicate across endpoints by room_id or id
        const map = new Map();
        [...statsData, ...roomsData, ...scheduledData].forEach((item) => {
          const key = item.room_id || item.room_code || item.id;
          if (key && !map.has(key)) {
            map.set(key, item);
          }
        });
        const combinedScheduled = Array.from(map.values());

        // Filter candidates scheduled for interview in upcoming time (today onwards & active status)
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

        const upcomingFiltered = combinedScheduled.filter((item) => {
          const status = String(item.status || item.candidate_status || item.room_status || '').toUpperCase();
          if (['COMPLETED', 'CANCELLED', 'REJECTED', 'FINISHED', 'EXPIRED', 'HIRED', 'FAILED'].includes(status)) {
            return false;
          }

          const rawDate = item.scheduled_date || item.date || item.scheduledDate;
          if (!rawDate) return true;

          const itemTime = parseUTC(rawDate).getTime();
          return itemTime >= startOfToday;
        }).sort((a, b) => {
          const aDate = a.scheduled_date || a.date || a.scheduledDate;
          const bDate = b.scheduled_date || b.date || b.scheduledDate;
          const aTime = aDate ? parseUTC(aDate).getTime() : Number.MAX_SAFE_INTEGER;
          const bTime = bDate ? parseUTC(bDate).getTime() : Number.MAX_SAFE_INTEGER;
          return aTime - bTime;
        });

        setUpcomingInterviews(upcomingFiltered);

        // Determine requisitions count from overview-stats or directly from global list
        const totalReqs = Number(reqData?.totalRequisitions !== undefined ? reqData.totalRequisitions : globalReqData.length) || 0;
        const openReqs = Number(reqData?.openRequisitions !== undefined ? reqData.openRequisitions : globalReqData.filter(r => ['PUBLISHED', 'OPEN', 'BU_APPROVED'].includes(r.status)).length) || 0;

        setStats({
          totalRequisitions: totalReqs,
          openRequisitions: openReqs,
          totalScreened: candData.length || 0,
          totalScheduled: upcomingFiltered.length || 0,
          totalExits: exitsData.length || 0,
          pendingExits: exitsData.filter(e => e.exit_status === 'EXIT_PENDING').length || 0
        });
      } catch (err) {
        console.warn('⚠️ Failed to load overview stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOverviewStats();
  }, []);

  const handleCopyLink = (link, id) => {
    if (navigator.clipboard && link) {
      navigator.clipboard.writeText(link);
      setCopiedLinkId(id);
      setTimeout(() => setCopiedLinkId(null), 2000);
    }
  };

  const handleDeleteInterview = async (roomId) => {
    if (!roomId) return;
    if (!window.confirm('Are you sure you want to delete this scheduled interview room?')) return;
    try {
      await api.delete(`/api/auth/interview-rooms/${roomId}`);
      setUpcomingInterviews(prev => prev.filter(item => (item.room_id || item.id) !== roomId));
    } catch (err) {
      console.error('Failed to delete room:', err);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <header style={styles.header}>
          <h2 style={styles.title}>Welcome back, HR Admin</h2>
          <p style={styles.subtitle}>Here is a high-level summary of your hiring funnel, active requisitions, and exit clearances.</p>
        </header>

        {/* Shimmer Stats Cards */}
        <StatsGridSkeleton count={4} />

        {/* Shimmer Two Columns */}
        <div style={styles.contentGrid}>
          <div style={styles.chartCard}>
            <Skeleton width="180px" height="20px" borderRadius="6px" style={{ marginBottom: '8px' }} />
            <Skeleton width="280px" height="13px" borderRadius="4px" style={{ marginBottom: '24px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <Skeleton width="100%" height="28px" borderRadius="8px" />
              <Skeleton width="100%" height="28px" borderRadius="8px" />
              <Skeleton width="100%" height="28px" borderRadius="8px" />
              <Skeleton width="100%" height="28px" borderRadius="8px" />
            </div>
          </div>

          <div style={styles.actionsCard}>
            <Skeleton width="140px" height="20px" borderRadius="6px" style={{ marginBottom: '8px' }} />
            <Skeleton width="240px" height="13px" borderRadius="4px" style={{ marginBottom: '20px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Skeleton width="100%" height="52px" borderRadius="12px" />
              <Skeleton width="100%" height="52px" borderRadius="12px" />
              <Skeleton width="100%" height="52px" borderRadius="12px" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h2 style={styles.title}>Welcome back, HR Admin</h2>
        <p style={styles.subtitle}>Here is a high-level summary of your hiring funnel, active requisitions, and exit clearances.</p>
      </header>

      {/* Grid Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIconWrapper}>💼</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Active Requisitions</span>
            <span style={styles.statValue}>{stats.openRequisitions} / {stats.totalRequisitions}</span>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIconWrapper, backgroundColor: '#dbeafe', color: '#2563eb' }}>📄</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Screened Candidates</span>
            <span style={styles.statValue}>{stats.totalScreened}</span>
          </div>
        </div>

        <div style={{ ...styles.statCard }}>
          <div style={{ ...styles.statIconWrapper, backgroundColor: '#fef3c7', color: '#d97706' }}>📅</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Interviews Scheduled</span>
            <span style={styles.statValue}>{stats.totalScheduled}</span>
          </div>
        </div>

        <div style={{ ...styles.statCard }}>
          <div style={{ ...styles.statIconWrapper, backgroundColor: '#fee2e2', color: '#dc2626' }}>🚪</div>
          <div style={styles.statInfo}>
            <span style={styles.statLabel}>Pending Exit Clearances</span>
            <span style={styles.statValue}>{stats.pendingExits}</span>
          </div>
        </div>
      </div>

      {/* Two Columns: Upcoming Interview Actions & Quick Actions */}
      <div style={styles.contentGrid}>
        {/* Upcoming Interview Actions Card */}
        <div style={styles.chartCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={styles.sectionTitle}>Upcoming interview actions</h3>
              <p style={{ ...styles.sectionSubtitle, margin: '4px 0 0 0' }}>Next scheduled interviews for your review.</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={styles.upcomingBadge}>Upcoming {upcomingInterviews.length}</span>
              <button 
                onClick={() => navigate('/hr-dashboard/interviews')}
                style={styles.viewAllBtn}
              >
                View All →
              </button>
            </div>
          </div>

          {upcomingInterviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#64748b', fontSize: '14px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <span style={{ fontSize: '32px', marginBottom: '8px' }}>📅</span>
              <p style={{ margin: 0, fontWeight: '500' }}>No upcoming interviews scheduled</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, overflowY: 'auto', maxHeight: '360px', paddingRight: '4px' }}>
              {upcomingInterviews.map((item, idx) => {
                const roomId = item.room_id || item.room_code || item.id;
                const status = String(item.status || item.candidate_status || item.room_status || 'SCHEDULED').toUpperCase();
                const statusTone = status === 'SCHEDULED'
                  ? { bg: '#eef2ff', fg: '#4338ca', border: '#c7d2fe' }
                  : { bg: '#fff7ed', fg: '#9a3412', border: '#fdba74' };

                const candidateJoinLink = item.candidate_link || (roomId ? generateCandidateLink(roomId) : '');
                const interviewerJoinLink = item.interviewer_link || (roomId && item.interview_type !== 'AI' ? generateInterviewerLink(roomId) : '');

                const scheduledDateStr = item.scheduled_date
                  ? parseUTC(item.scheduled_date).toLocaleString([], {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Schedule pending';

                const candidateName = item.candidate_name || item.candidateName || item.candidate || 'Candidate not set';

                return (
                  <div key={roomId || idx} style={styles.upcomingItem}>
                    <span style={styles.upcomingStrip} aria-hidden="true" />
                    <div style={styles.upcomingItemTop}>
                      <div style={styles.upcomingIdentity}>
                        <div style={styles.upcomingCandidate}>{candidateName}</div>
                        <div style={styles.upcomingMeta}>Room: {roomId || 'N/A'}</div>
                      </div>
                      <div style={styles.upcomingTopActions}>
                        <span
                          style={{
                            ...styles.upcomingStatus,
                            background: statusTone.bg,
                            color: statusTone.fg,
                            borderColor: statusTone.border,
                          }}
                        >
                          {status}
                        </span>
                        {roomId && (
                          <button
                            type="button"
                            onClick={() => handleDeleteInterview(roomId)}
                            style={styles.upcomingDeleteBtn}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={styles.upcomingScheduleRow}>
                      <span style={styles.upcomingScheduleLabel}>{status}</span>
                      <span style={styles.upcomingScheduleValue}>{scheduledDateStr}</span>
                    </div>

                    <div style={styles.upcomingLinksRow}>
                      {candidateJoinLink && (
                        <div style={{ ...styles.upcomingPillLink, ...styles.candidatePill }}>
                          <a
                            href={candidateJoinLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M23 7l-7 5 7 5V7z" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                            Candidate Link
                          </a>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleCopyLink(candidateJoinLink, `cand_${roomId}`); }}
                            style={styles.copyIconButtonCandidate}
                            title="Copy link"
                          >
                            {copiedLinkId === `cand_${roomId}` ? (
                              <span style={{ fontSize: '11px', fontWeight: '700' }}>✓</span>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                              </svg>
                            )}
                          </button>
                        </div>
                      )}

                      {interviewerJoinLink && (
                        <div style={{ ...styles.upcomingPillLink, ...styles.interviewerPill }}>
                          <a
                            href={interviewerJoinLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M23 7l-7 5 7 5V7z" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                            Interviewer Link
                          </a>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); handleCopyLink(interviewerJoinLink, `inter_${roomId}`); }}
                            style={styles.copyIconButtonInterviewer}
                            title="Copy link"
                          >
                            {copiedLinkId === `inter_${roomId}` ? (
                              <span style={{ fontSize: '11px', fontWeight: '700' }}>✓</span>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                              </svg>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick Actions Card */}
        <div style={styles.actionsCard}>
          <h3 style={styles.sectionTitle}>Quick Workflows</h3>
          <p style={styles.sectionSubtitle}>Jump straight to active HR tasks.</p>
          
          <div style={styles.actionsList}>
            <button onClick={() => navigate('/hr-dashboard/jd-builder')} style={styles.actionBtn}>
              <span style={styles.actionIcon}>➕</span>
              <div style={styles.actionTextWrapper}>
                <span style={styles.actionTitle}>Create Requisition</span>
                <span style={styles.actionDesc}>Build new JDs and publish open positions.</span>
              </div>
            </button>

            <button onClick={() => navigate('/hr-dashboard/screening')} style={styles.actionBtn}>
              <span style={styles.actionIcon}>📤</span>
              <div style={styles.actionTextWrapper}>
                <span style={styles.actionTitle}>Upload Resumes</span>
                <span style={styles.actionDesc}>Bulk parse and scan candidate resumes with Gemini.</span>
              </div>
            </button>

            <button onClick={() => navigate('/hr-dashboard/interviews?tab=create')} style={styles.actionBtn}>
              <span style={styles.actionIcon}>📅</span>
              <div style={styles.actionTextWrapper}>
                <span style={styles.actionTitle}>Interview Schedule</span>
                <span style={styles.actionDesc}>Create interview room and invite candidates.</span>
              </div>
            </button>

            <button onClick={() => navigate('/hr-dashboard/exits')} style={styles.actionBtn}>
              <span style={styles.actionIcon}>📋</span>
              <div style={styles.actionTextWrapper}>
                <span style={styles.actionTitle}>Manage Clearances</span>
                <span style={styles.actionDesc}>Verify exit checklist items and offboard members.</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    textAlign: 'left'
  },
  header: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '800',
    color: '#0f172a',
    letterSpacing: '-0.02em'
  },
  subtitle: {
    margin: '6px 0 0 0',
    fontSize: '14.5px',
    color: '#64748b',
    lineHeight: '1.5'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px'
  },
  statCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
  },
  statIconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: '#dcfce7',
    color: '#15803d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  statInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  statLabel: {
    fontSize: '12.5px',
    fontWeight: '600',
    color: '#64748b'
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '800',
    color: '#0f172a',
    marginTop: '4px'
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px'
  },
  chartCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column'
  },
  actionsCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '17px',
    fontWeight: '700',
    color: '#0f172a'
  },
  sectionSubtitle: {
    margin: '4px 0 20px 0',
    fontSize: '13px',
    color: '#64748b'
  },
  upcomingBadge: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#4338ca',
    backgroundColor: '#eef2ff',
    padding: '3px 10px',
    borderRadius: '999px',
    border: '1px solid #c7d2fe'
  },
  viewAllBtn: {
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  upcomingItem: {
    position: 'relative',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    padding: '12px 14px 12px 18px',
    boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
    textAlign: 'left'
  },
  upcomingStrip: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',
    borderTopLeftRadius: '12px',
    borderBottomLeftRadius: '12px',
    background: 'linear-gradient(180deg, #22d3ee 0%, #2563eb 100%)'
  },
  upcomingItemTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px'
  },
  upcomingIdentity: {
    minWidth: 0
  },
  upcomingCandidate: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#0f172a',
    marginBottom: '2px',
    lineHeight: 1.2
  },
  upcomingMeta: {
    fontSize: '11px',
    color: '#64748b'
  },
  upcomingTopActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0
  },
  upcomingStatus: {
    fontSize: '10px',
    fontWeight: '800',
    padding: '4px 10px',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    border: '1px solid transparent',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  upcomingDeleteBtn: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#dc2626',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    padding: '3px 8px',
    cursor: 'pointer'
  },
  upcomingScheduleRow: {
    marginTop: '6px',
    display: 'inline-flex',
    gap: '6px',
    alignItems: 'center',
    borderRadius: '999px',
    padding: '3px 10px',
    backgroundColor: '#ecfeff',
    border: '1px solid #67e8f9'
  },
  upcomingScheduleLabel: {
    fontSize: '9px',
    color: '#0e7490',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  upcomingScheduleValue: {
    fontSize: '12px',
    color: '#0f172a',
    fontWeight: '700'
  },
  upcomingLinksRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '8px'
  },
  upcomingPillLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px 4px 14px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '700',
    textDecoration: 'none',
    cursor: 'pointer'
  },
  candidatePill: {
    backgroundColor: '#6366f1',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)'
  },
  interviewerPill: {
    backgroundColor: '#ffffff',
    color: '#4338ca',
    border: '1px solid #c7d2fe',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)'
  },
  copyIconButtonCandidate: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    cursor: 'pointer'
  },
  copyIconButtonInterviewer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    cursor: 'pointer'
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
    justifyContent: 'center'
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    borderStyle: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left'
  },
  actionIcon: {
    fontSize: '24px',
    flexShrink: 0
  },
  actionTextWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  actionTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#0f172a'
  },
  actionDesc: {
    fontSize: '11.5px',
    color: '#64748b',
    lineHeight: '1.4'
  }
};
